import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  // useState,
} from 'react';
import { Link } from 'react-router-dom';
import { HStack, Button, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';
import { useMutation } from '@apollo/client';
import { colors, focusVisibleProps } from '../../../../../App/ChakraTheme';
import getNewLabUrl from '../../../../../helpers/getNewLabUrl';

import {
  DownloadIcon,
  // PinIcon,
  // UnpinIcon,
  FullscreenIcon,
  ShareIcon,
  CheckbocCheckedIcon,
  CheckboxIcon,
  AvatarIcon,
  SaveIcon,
  AnalyticsIcon,
  PresentIcon,
  // EditIcon,
} from '../../../../../components/icons';
import { MediaTypeIcon } from '../../../../../scripts/getMediaTypeIcon';
import getContentTypeName from '../../../../../scripts/getContentTypeName';

import { ConfirmBtn } from '../../../components/ConfirmBtn';
import Loader from '../../../../../components/Loader';
import { ClassPage } from '../../../../../modules/app';

import { trackAction } from '../../../../../helpers/trackAction';
import { Role, User } from '../../../../../context/AuthProvider';
import {
  ChalkUp,
  Mutation,
  Pinning,
  Share,
  StudyGroup,
  TrackContent,
  TrackModule,
  TrackUnit,
} from '../../../../../generated/graphqlModels';
import {
  CREATE_CHALKUP,
  CREATE_SHARE,
  // CREATE_PINNING,
  DELETE_CHALKUP,
  // DELETE_PINNING,
  DELETE_SHARE,
} from '../queries';
import { State } from '../hooks/useGetUnitDataGql';
import useScrollDirection from '../../../../../hooks/useScrollDirection';

interface ToolbarProps {
  context: User;
  notFound: boolean;
  programId: string;
  trackId?: string;
  trackUnit?: TrackUnit;
  trackModule?: TrackModule;
  trackContent?: TrackContent;
  pinned?: Pinning;
  pinnings?: Pinning[];
  studyGroup?: StudyGroup;
  isShared: boolean;
  shares?: Share[];
  isChalkedUp: boolean;
  isChalkupsLoading: boolean;
  chalkups?: ChalkUp[];
  isTeacherGuideDisplayed: boolean;
  isSaveAvailable: boolean;
  isSaving: boolean;
  iframeRef: React.MutableRefObject<HTMLIFrameElement | null>;
  toggleComplete: () => void;
  updateParentState: (newState: Partial<State>) => void;
  shouldGoNext: number;
  setShouldGoNext: (_: number) => void;
  popup: Window | null;
  setPopup: (_: Window | null) => void;
  presentationEnabled: boolean;
  setPresentationEnabled: (_: boolean) => void;
  isProgramUsingNewContentRendering: boolean;
  isProgramContentRenderingLoading: boolean;
  sidebarOpened: boolean;
}

const Toolbar = forwardRef((props: ToolbarProps, ref) => {
  const {
    programId,
    trackId,
    context,
    notFound,
    trackUnit,
    trackModule,
    trackContent,
    // pinned,
    // pinnings,
    studyGroup,
    isShared,
    shares,
    isChalkedUp,
    isChalkupsLoading,
    chalkups,
    isTeacherGuideDisplayed,
    isSaveAvailable,
    isSaving,
    iframeRef,
    toggleComplete,
    updateParentState,
    shouldGoNext,
    setShouldGoNext,
    popup,
    setPopup,
    presentationEnabled,
    setPresentationEnabled,
    // isProgramUsingNewContentRendering,
    // isProgramContentRenderingLoading,
    sidebarOpened,
  } = props;

  const titleRef = useRef(null);
  const [completeContentMutation] = useMutation<Mutation>(CREATE_CHALKUP);
  const [shareContentMutation] = useMutation<Mutation>(CREATE_SHARE);
  // const [pinContentMutation] = useMutation<Mutation>(CREATE_PINNING);
  const [unChalkUpMutation] = useMutation<Mutation>(DELETE_CHALKUP);
  // const [unPinMutation] = useMutation<Mutation>(DELETE_PINNING);
  const [unShareMutation] = useMutation<Mutation>(DELETE_SHARE);
  const { scrollDirection, scrollY } = useScrollDirection();
  const isFloating = scrollY > 150;
  const { t } = useTranslation();

  useImperativeHandle(ref, () => ({
    chalkUpContent,
    removeChalkUpContent,
  }));

  const toggleTeacherGuide = () => {
    updateParentState({
      isTeacherGuideDisplayed: !isTeacherGuideDisplayed,
    });
  };

  // TODO: Use shared method for this from EditStudyGroup.js
  const shareContent = () => {
    if (studyGroup && trackContent && shares) {
      updateParentState({ isSharesLoading: true });
      shareContentMutation({
        variables: {
          contentId: trackContent.content.id,
          studyGroupId: studyGroup.id,
        },
      })
        .then((data) => {
          if (data.data && data.data.shareContent) {
            updateParentState({
              shares: [...shares, data.data.shareContent],
            });
          }
        })
        .catch((error) => {
          // const { response } = error;
          console.error(error);
        })
        .finally(() => {
          updateParentState({ isSharesLoading: false });
        });
    }
  };

  const unshareContent = () => {
    if (shares && trackContent) {
      const share = shares.find(
        (s) => s.contentId === trackContent?.content.id,
      );
      if (share) {
        updateParentState({ isSharesLoading: true });
        unShareMutation({
          variables: { shareId: share.id },
        })
          .then((data) => {
            updateParentState({
              shares: shares.filter((c) => c.id !== share.id),
            });
            // if (data.data && data.data.deleteChalkUps?.length > 0) {
            //   trackAction(
            //     {
            //       action_type: 'unshared',
            //       action_object: {
            //         type: 'content',
            //         pk: trackContent.content.id,
            //       },
            //       study_group: { pk: studyGroup?.id },
            //     },
            //     context?.user_actions,
            //   );
            // }
          })
          .catch((error) => {
            // const { response } = error;
            console.error(error);
          })
          .finally(() => {
            updateParentState({ isSharesLoading: false });
          });
      }
    }
  };

  // const pinContent = () => {
  //   if (studyGroup && trackContent && pinnings) {
  //     updateParentState({ isPinningsLoading: true });
  //     pinContentMutation({
  //       variables: {
  //         contentId: trackContent.content.id,
  //         studyGroupId: studyGroup.id,
  //       },
  //     })
  //       .then((data) => {
  //         if (data.data && data.data.pinContent) {
  //           trackAction(
  //             {
  //               action_type: 'pinned',
  //               action_object: { type: 'content', pk: trackContent.content.id },
  //               study_group: { pk: studyGroup.id },
  //             },
  //             context.user_actions,
  //           );
  //           updateParentState({
  //             pinnings: [...pinnings, data.data.pinContent],
  //           });
  //         }
  //       })
  //       .catch((error) => {
  //         // const { response } = error;
  //         console.error(error);
  //       })
  //       .finally(() => {
  //         updateParentState({ isPinningsLoading: false });
  //       });
  //   }
  // };

  // const deletePinnedContent = () => {
  //   if (pinnings && trackContent) {
  //     const pinning = pinnings.find(
  //       (s) => s.contentId === trackContent?.content.id,
  //     );
  //     if (pinning) {
  //       unPinMutation({
  //         variables: { pinId: pinning.id },
  //       })
  //         .then((data) => {
  //           updateParentState({
  //             pinnings: pinnings.filter((c) => c.id !== pinning.id),
  //           });
  //           if (data.data && data.data.deleteChalkUps?.length > 0) {
  //             trackAction(
  //               {
  //                 action_type: 'unpinned',
  //                 action_object: {
  //                   type: 'content',
  //                   pk: trackContent.content.id,
  //                 },
  //                 study_group: { pk: studyGroup?.id },
  //               },
  //               context?.user_actions,
  //             );
  //           }
  //         })
  //         .catch((error) => {
  //           // const { response } = error;
  //           console.error(error);
  //         })
  //         .finally(() => {
  //           updateParentState({ isPinningsLoading: false });
  //         });
  //     }
  //   }
  // };

  const chalkUpContent = () => {
    if (studyGroup && trackContent && chalkups) {
      updateParentState({ isChalkupsLoading: true });
      completeContentMutation({
        variables: {
          contentId: trackContent.content.id,
          studyGroupId: studyGroup.id || null,
        },
      })
        .then((data) => {
          if (data.data && data.data.completeContent) {
            trackAction(
              {
                action_type: 'completed',
                action_object: { type: 'content', pk: trackContent.content.id },
                study_group: { pk: studyGroup.id },
              },
              context.user_actions,
            );
            updateParentState({
              chalkups: [...chalkups, data.data.completeContent],
            });
          }
        })
        .catch((error) => {
          // const { response } = error;
          console.error(error);
        })
        .finally(() => {
          updateParentState({ isChalkupsLoading: false });
        });
    }
  };

  const removeChalkUpContent = () => {
    if (chalkups && trackContent) {
      const chalkUp = chalkups.find(
        (s) => s.contentId === trackContent?.content.id,
      );
      if (chalkUp) {
        unChalkUpMutation({
          variables: { chalkUpId: chalkUp.id },
          // update(cache, { data }) {
          //   console.log(studyGroup);
          //   const cacheData = cache.readQuery<Query>({
          //     query: GET_STUDY_GROUP,
          //     variables: { studyGroupId: studyGroup?.id },
          //   });
          //   console.log(cacheData);
          //   if (cacheData && data) {
          //     const { studyGroup } = cacheData;
          //     cache.writeQuery({
          //       query: GET_STUDY_GROUP,
          //       data: {
          //         studyGroup: {
          //           ...studyGroup,
          //           chalkups: studyGroup.chalkups.filter(
          //             (c: { id: string }) => c.id !== chalkUp.id,
          //           ),
          //         },
          //       },
          //     });
          //   }
          // const normalizedId = cache.identify({
          //   id: chalkUp.id,
          //   __typename: 'ChalkUp',
          // });
          // cache.evict({ id: normalizedId });
          // cache.gc();
          // },
        })
          .then((data) => {
            // console.log('here', chalkups);
            updateParentState({
              chalkups: chalkups.filter((c) => c.id !== chalkUp.id),
            });
            if (data.data && data.data.deleteChalkUps?.length > 0) {
              trackAction(
                {
                  action_type: 'uncompleted',
                  action_object: {
                    type: 'content',
                    pk: trackContent.content.id,
                  },
                  study_group: { pk: studyGroup?.id },
                },
                context?.user_actions,
              );
            }
          })
          .catch((error) => {
            // const { response } = error;
            console.error(error);
          })
          .finally(() => {
            updateParentState({ isChalkupsLoading: false });
          });
      }
    }
  };

  const trackDownloadContent = () => {
    if (trackContent) {
      trackAction(
        {
          action_type: 'download',
          action_object: { type: 'content', pk: trackContent.content.id },
          study_group: { pk: studyGroup?.id },
        },
        context?.user_actions,
      );
    }
  };

  const saveAll = () => {
    // @ts-ignore
    const iframeDoc = iframeRef.current.contentWindow.document;
    const saveAllBtn = iframeDoc.querySelector('#save-all-btn');
    const wrappers = iframeDoc.querySelectorAll('.question-wrapper');
    const assessmentId = iframeDoc.querySelector('#assessment_id')?.innerHTML;

    let answers = {};
    // @ts-ignore
    for (const cur of wrappers) {
      if (cur.id !== '') {
        const questionName = cur.id;

        const questionId = questionName.split('-')[1].trim();
        const answerIframe = iframeDoc.querySelector(
          `#answer-${questionId}_ifr`,
        );
        if (answerIframe) {
          // @ts-ignore
          const answer = answerIframe.contentWindow.document.querySelector(
            `[data-id='answer-${questionId}']`,
          )?.innerHTML;
          // @ts-ignore
          answers[questionName] = answer;
        }
      }
    }

    // @ts-ignore
    saveAllBtn?.click();
    if (saveAllBtn && trackContent) {
      trackAction(
        {
          action_type: 'answered_ass',
          action_object: { type: 'content', pk: trackContent.content.id },
          data: { assessmentId, answers },
          study_group: { pk: studyGroup?.id },
        },
        context?.user_actions,
      );
    }
  };

  const msgListener = (event: any) => {
    if (event.data === 'previous') {
      setShouldGoNext(-1);
    } else if (event.data === 'next') {
      setShouldGoNext(1);
    } else if (event.data === 'close') {
      removePresentationListeners();
      setPopup(null);
    }
  };

  const keyListener = (event: any, pop: any) => {
    if (event.key === 'ArrowLeft' && shouldGoNext === 0) {
      setShouldGoNext(-1);
    }
    if (event.key === 'ArrowRight' && shouldGoNext === 0) {
      setShouldGoNext(1);
    }
    if (event.key === 'Escape') {
      pop.close();
      setPopup(null);
      removePresentationListeners();
    }
  };

  const removePresentationListeners = () => {
    // @ts-ignore
    document.removeEventListener('keyup', keyListener);
    document.removeEventListener('message', msgListener);
  };

  const openPresentationMode = () => {
    removePresentationListeners();
    const pop = window.open(
      '/presentation-popup',
      undefined,
      'popup=true,width=640,height=480',
    );
    setPopup(pop);
    window.addEventListener('message', msgListener);
    document.addEventListener('keyup', (event) => {
      keyListener(event, pop);
    });
  };

  const isCmsContent = trackContent && (trackContent.content.mediaType === 'cms' || (trackContent.forwardContent && trackContent.forwardContent.mediaType === 'cms'));

  useEffect(() => {
    setPresentationEnabled(false);
    if (popup) {
      popup.close();
      setPopup(null);
    }
  }, [trackContent]);

  if (notFound) {
    return null;
  }

  if (!context || !trackContent || !trackModule || !trackUnit) {
    return <Loader />;
  }

  return (
    <>
      <HStack
        w="full"
        className={`v-center content-controls toolbar-scroll sticky ${
          scrollDirection === 'down' ? 'hide' : ''
        }`}
        position="sticky"
        top="0"
        spacing={0}
        gap={2}
        py={4}
        pl={scrollY > 150 || sidebarOpened ? 0 : 20}
        background="white"
        zIndex="10"
        style={{
          boxShadow: isFloating ? '0 4px 6px -6px #222' : undefined,
        }}
        sx={{
          '&:before': {
            content: '""',
            background: '#fff',
            position: 'absolute',
            height: '100%',
            width: '100%',
            left: '-25vw',
            top: 0,
            zIndex: -1,
          },
        }}
      >
        {trackContent.content.isDownloadable && (
          <DonwloadButton
          trackContent={trackContent}
          onDownload={trackDownloadContent}
        />)}

        {trackContent.content.isShareable &&
          context.roles.includes(Role.teacher) &&
          (isShared ? (
            <>
              <ConfirmBtn
                message={'Are you sure?'}
                colorScheme="orange"
                onConfirm={() => unshareContent()}
                // icon={<ShareIcon boxSize={5} />}
                tippyContent={t('toolbar.tippy.sharedContent')}
                textColor="tomato"
                {...focusVisibleProps}
              >
                <ShareIcon boxSize={5} />
                <span className="hide-tablet">
                  {t('toolbar.hideFromStudents')}
                </span>
              </ConfirmBtn>
            </>
          ) : (
            <>
              <Tippy
                arrow={true}
                theme="light"
                animation="scale"
                delay={800}
                content={t('toolbar.tippy.shareWithStudents')}
              >
                <Button
                  colorScheme="blue"
                  data-cy-share
                  onClick={() => shareContent()}
                  leftIcon={<ShareIcon boxSize={5} />}
                  {...focusVisibleProps}
                >
                  <span className="hide-tablet">
                    {t('toolbar.shareWithStudents')}
                  </span>
                </Button>
              </Tippy>
            </>
          ))}
        {trackContent.content.mediaType !== 'file' ? (
          <Tippy
            arrow={true}
            theme="light"
            animation="scale"
            delay={800}
            content={t('toolbar.tippy.openFullscreen')}
          >
            <Button
              colorScheme="blue"
              data-cy-fullscreen
              variant="outline"
              aria-label={t('toolbar.tippy.openFullscreen')}
              onClick={() => {
                // @ts-ignore
                iframeRef.current.webkitRequestFullScreen();
              }}
              leftIcon={<FullscreenIcon boxSize={5} />}
              {...focusVisibleProps}
            >
              <span className="hide-tablet">{t('toolbar.fullscreen')}</span>
            </Button>
          </Tippy>
        ) : null}

        {
          // TODO Needs a more specific condition:
          context.roles.includes(Role.teacher) ||
          context.roles.includes(Role.student) ? (
            isChalkupsLoading ? (
              <Tippy
                arrow={true}
                theme="light"
                animation="scale"
                delay={800}
                content={t('toolbar.tippy.loading')}
              >
                <Button
                  colorScheme="blue"
                  isLoading={true}
                  onClick={() => {}}
                  {...focusVisibleProps}
                >
                  <Loader />
                </Button>
              </Tippy>
            ) : isChalkedUp ? (
              <Tippy
                arrow={true}
                theme="light"
                animation="scale"
                delay={800}
                content={t('toolbar.tippy.completed')}
              >
                <Button
                  colorScheme="green"
                  data-cy-uncomplete
                  style={{ background: colors.limegreen }}
                  onClick={() => toggleComplete()}
                  leftIcon={<CheckbocCheckedIcon boxSize={5} />}
                  {...focusVisibleProps}
                >
                  <span className="hide-tablet">{t('toolbar.completed')}</span>
                </Button>
              </Tippy>
            ) : isCmsContent ? null : (
              <Tippy
                arrow={true}
                theme="light"
                animation="scale"
                delay={800}
                content={t('toolbar.tippy.markContentAsComplete')}
              >
                <Button
                  colorScheme="blue"
                  data-cy-complete
                  className="complete-btn"
                  onClick={() => toggleComplete()}
                  leftIcon={<CheckboxIcon boxSize={5} />}
                  {...focusVisibleProps}
                >
                  <span className="hide-tablet">{t('toolbar.complete')}</span>
                </Button>
              </Tippy>
            )
          ) : null
        }
        {context.roles.includes(Role.teacher) && isCmsContent && (
          <Tippy
            arrow={true}
            theme="light"
            animation="scale"
            delay={800}
            content={
              isTeacherGuideDisplayed
                ? t('toolbar.tippy.hideTeacherGuide')
                : t('toolbar.tippy.showTeacherGuide')
            }
          >
            <Button
              data-cy-hide-teacher-guide
              // style={{
              //   background: isTeacherGuideDisplayed
              //     ? colors.darkblue
              //     : colors.tomato,
              // }}
              colorScheme={isTeacherGuideDisplayed ? 'orange' : 'blue'}
              variant="outline"
              aria-label={isTeacherGuideDisplayed ? t('toolbar.tippy.hideTeacherGuide') : t('toolbar.tippy.showTeacherGuide')}
              onClick={() => toggleTeacherGuide()}
              leftIcon={<AvatarIcon boxSize={5} />}
              {...focusVisibleProps}
            >
              <span className="hide-tablet">
                {isTeacherGuideDisplayed
                  ? t('toolbar.hideTeacherGuide')
                  : t('toolbar.showTeacherGuide')}
              </span>
            </Button>
          </Tippy>
        )}
        {trackContent.content.mediaType === 'assessment' &&
          (isSaving ? (
            <Button
              style={{ minWidth: '150px', justifyContent: 'center' }}
              title={'loading'}
              onClick={() => {}}
            >
              <Loader color="#FFF" />
            </Button>
          ) : (
            <Button
              disabled={!isSaveAvailable}
              title={'Save all'}
              variant="outline"
              onClick={saveAll}
              leftIcon={<SaveIcon boxSize={5} />}
              {...focusVisibleProps}
            >
              <span className="hide-tablet">{t('toolbar.saveAll')}</span>
            </Button>
          ))}
        {context.roles.includes(Role.teacher) && studyGroup && (
          <Tippy
            arrow={true}
            theme="light"
            animation="scale"
            delay={800}
            content={t('toolbar.tippy.openContentOnAnalytics')}
          >
            <Link
              aria-label={t('toolbar.tippy.openContentOnAnalytics')}
              to={ClassPage.getRoute(
                programId,
                'analytics',
                `?study-group-id=${studyGroup.id}${
                  trackId ? `&track-id=${trackId}` : ''
                }&analytics-unit-id=${trackUnit?.unit.id}&analytics-module-id=${
                  trackModule?.module.id
                }&analytics-content-id=${
                  trackContent.content.id
                }&analytics-tab-index=1`,
              )}
            >
              <Button
                tabIndex={-1}
                aria-hidden="true"
                colorScheme="blue"
                className="analytics-btn"
                variant="outline"
                data-cy-analytics
                leftIcon={<AnalyticsIcon boxSize={5} />}
                {...focusVisibleProps}
              >
                <span className="hide-tablet">{t('toolbar.analytics')}</span>
              </Button>
            </Link>
          </Tippy>
        )}

        {context.roles.includes(Role.teacher) &&
          presentationEnabled &&
          isCmsContent && (
            <Tippy
              arrow={true}
              theme="light"
              animation="scale"
              delay={800}
              content={t('toolbar.tippy.openPresentation')}
            >
              <Button
                colorScheme="blue"
                className="presentation-btn"
                variant="outline"
                data-cy-presentation
                onClick={openPresentationMode}
                leftIcon={<PresentIcon boxSize={5} />}
                {...focusVisibleProps}
              >
                <span className="hide-tablet">{t('toolbar.presentation')}</span>
              </Button>
            </Tippy>
          )}
        {context.roles.includes(Role.staff) && (
          
            <Tippy
              arrow={true}
              theme="light"
              animation="scale"
              delay={800}
              content={t('toolbar.tippy.openContentInCMS')}
            >
              <a
                href={`/admin/syllabus/content/${trackContent.content.id}/change/`}
                target="blank"
              >
                <Button
                  tabIndex={-1}
                  variant="outline"
                  colorScheme="blue"
                  {...focusVisibleProps}
                >
                  {t('toolbar.openInCMS')}
                </Button>
              </a>
            </Tippy>
            )}
            {(context.roles.includes(Role.staff) || context.roles.includes(Role.organization_user)) && (
            <Tippy
              arrow={true}
              theme="light"
              animation="scale"
              delay={800}
              content={t('toolbar.tippy.openContentInNewCMS')}
            >
              <a
                href={`${getNewLabUrl()}/edit/${
                  trackContent.content.id
                }?page=1&token=${context.token}`}
                target="blank"
              >
                <Button
                  tabIndex={-1}
                  variant="outline"
                  colorScheme="blue"
                  {...focusVisibleProps}
                >
                  {t('toolbar.openInNewCMS')}
                </Button>
              </a>
            </Tippy>
        )}
      </HStack>
      <HStack ref={titleRef} style={{ width: '100%', maxWidth: '100%' }} pb={4}>
        <Tippy
          arrow={true}
          theme="light"
          animation="scale"
          delay={800}
          content={getContentTypeName(trackContent.content.mediaType)}
        >
          <div>
            <MediaTypeIcon
              mediaType={trackContent.content.mediaType}
              boxSize="24px"
            />
          </div>
        </Tippy>
        <Text as="h2" textStyle="h4" data-cy="content-title">
          {trackModule.module.name} - {trackContent.content.name}
        </Text>
      </HStack>
    </>
  );
});

export default Toolbar;

function DonwloadButton({
  trackContent,
  onDownload,
}: {
  trackContent: TrackContent;
  onDownload: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  let linkHref: string = '';
  let onClick: (() => void) | undefined;
  let openBlank: boolean = false;

  if (trackContent.content.mediaType === 'cms') {
    linkHref = `/cms_content/${trackContent.content.id}/?toolbar_off&print=true`;
    openBlank = true;
  } else if (trackContent.content.mediaType === 'forwarding') {
    if (trackContent.forwardContent?.mediaType === 'cms') {
      linkHref = `/cms_content/${trackContent.forwardContent.id}/?toolbar_off&print=true`;
      openBlank = true;
    } else if (
      trackContent.forwardContent?.mediaType === 'file' ||
      (trackContent.forwardContent?.isDownloadable &&
        trackContent.forwardContent?.mediaType !== 'cms')
    ) {
      linkHref = trackContent.forwardContent?.fileUrl ?? '';
      onClick = onDownload;
    }
  } else if (
    (trackContent.content.isDownloadable &&
      trackContent.content.mediaType !== 'cms') ||
    trackContent.content.mediaType === 'file'
  ) {
    const forwardContent = trackContent?.forwardContent;
    const content = trackContent?.content;
    const fileUrl = forwardContent?.fileUrl ?? content?.fileUrl ?? '';
    linkHref = fileUrl;
    onClick = onDownload;
  }
  return (
    <Tippy
      arrow={true}
      theme="light"
      animation="scale"
      delay={800}
      content={t('toolbar.tippy.download')}
    >
      <a
        href={linkHref}
        target={openBlank ? '_blank' : undefined}
        rel={openBlank ? 'noreferrer' : undefined}
        aria-label={t('toolbar.download')}
      >
        <Button
          tabIndex={-1}
          aria-hidden="true"
          colorScheme="blue"
          variant="outline"
          data-cy-download
          onClick={onClick}
          leftIcon={<DownloadIcon boxSize={5} />}
        >
          <span className="hide-tablet">{t('toolbar.download')}</span>
        </Button>
      </a>
    </Tippy>
  );
}
